(() => {
  const reduced = () => matchMedia('(prefers-reduced-motion: reduce)').matches;
  const initTabs = (scope = document) => scope.querySelectorAll('[data-tt-tabs]').forEach(group => {
    if (group.dataset.ready) return;
    group.dataset.ready = 'true';
    const tabs = [...group.querySelectorAll('[role="tab"]')];
    const select = tab => tabs.forEach(item => {
      const active = item === tab;
      item.setAttribute('aria-selected', String(active));
      item.tabIndex = active ? 0 : -1;
      const panel = document.getElementById(item.getAttribute('aria-controls'));
      if (panel) panel.hidden = !active;
    });
    tabs.forEach((tab, index) => {
      tab.addEventListener('click', () => select(tab));
      tab.addEventListener('keydown', event => {
        const next = { ArrowRight: (index + 1) % tabs.length, ArrowLeft: (index - 1 + tabs.length) % tabs.length, Home: 0, End: tabs.length - 1 }[event.key];
        if (next === undefined) return;
        event.preventDefault(); select(tabs[next]); tabs[next].focus();
      });
    });
    if (tabs[0]) select(tabs[0]);
  });
  if (!customElements.get('tachan-tech-product')) customElements.define('tachan-tech-product', class extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      try {
        this.config = JSON.parse(this.querySelector('[data-tt-config]').textContent);
        this.variants = JSON.parse(this.querySelector('[data-tt-variants]').textContent);
      } catch { return; }
      this.form = this.querySelector('form.tt-product-form');
      this.select = this.querySelector('[data-tt-variant]');
      this.quantity = this.querySelector('[data-tt-quantity]');
      this.packCustom = this.querySelector('[data-tt-pack-custom]');
      this.packSelects = [...this.querySelectorAll('[data-tt-pack-variant]')];
      if (!this.form || !this.select || !this.quantity) return;
      this.ready = true;
      this.stockState = 'loading';
      this.controller = new AbortController();
      const options = { signal: this.controller.signal };
      this.formatter = new Intl.NumberFormat(this.config.locale || 'es-ES', { style: 'currency', currency: this.config.currency });
      this.querySelectorAll('[data-tt-swatches], [data-tt-offers], [data-tt-quantity-step], [data-tt-checkout], [data-tt-total-wrap]').forEach(node => { node.hidden = false; });
      this.select.addEventListener('change', () => this.updateVariant(true), options);
      this.quantity.addEventListener('input', () => this.updateTotals(), options);
      this.quantity.addEventListener('change', () => { this.quantity.value = this.count(); this.updateTotals(); }, options);
      this.querySelectorAll('[data-tt-color]').forEach(input => input.addEventListener('change', () => {
        const target = this.variants.find(variant => variant.options[0] === input.value && variant.options.slice(1).every((value, index) => value === this.variant.options[index + 1]));
        if (!target) { this.error(this.config.strings.soldOut); this.updateVariant(false); return; }
        this.select.value = String(target.id); this.updateVariant(true);
      }, options));
      this.querySelectorAll('[data-tt-pack]').forEach(input => input.addEventListener('change', () => { this.quantity.value = input.value; this.updateTotals(); }, options));
      this.packSelects.forEach((select, index) => select.addEventListener('change', () => {
        const variant = this.variants.find(item => String(item.id) === select.value);
        if (index === 0 && variant) { this.select.value = select.value; this.updateVariant(true); }
        else { this.updateTotals(); this.image(variant?.imageId); this.error(''); }
      }, options));
      this.querySelectorAll('[data-tt-quantity-step]').forEach(button => button.addEventListener('click', () => {
        this.quantity.value = Math.max(1, this.count() + Number(button.dataset.ttQuantityStep)); this.updateTotals();
      }, options));
      this.querySelector('[data-tt-stock-retry]')?.addEventListener('click', () => this.hydrateStock(true), options);
      this.form.addEventListener('submit', event => { event.preventDefault(); this.add(this.config.cartUrl); }, options);
      this.querySelector('[data-tt-checkout]')?.addEventListener('click', () => this.add(this.config.checkoutUrl), options);
      this.gallery = this.querySelector('[data-tt-gallery]');
      this.querySelectorAll('[data-tt-image]').forEach(button => button.addEventListener('click', () => this.image(button.dataset.ttImage), options));
      if (this.gallery && 'IntersectionObserver' in window) {
        this.galleryObserver = new IntersectionObserver(entries => entries.forEach(entry => {
          if (entry.isIntersecting) this.querySelectorAll('[data-tt-image]').forEach(dot => dot.setAttribute('aria-current', String(dot.dataset.ttImage === entry.target.dataset.imageId)));
        }), { root: this.gallery, threshold: 0.65 });
        this.gallery.querySelectorAll('[data-image-id]').forEach(slide => this.galleryObserver.observe(slide));
      }
      this.sticky = this.querySelector('[data-tt-sticky]');
      this.buyArea = this.querySelector('[data-tt-buy-area]');
      this.footer = document.querySelector('footer, .shopify-section-group-footer-group');
      if (this.sticky) {
        this.sticky.hidden = false;
        const schedule = () => {
          if (this.frame) return;
          this.frame = requestAnimationFrame(() => { this.frame = null; this.updateSticky(); });
        };
        window.addEventListener('scroll', schedule, { passive: true, signal: this.controller.signal });
        window.addEventListener('resize', schedule, options);
      }
      this.updateVariant(false); this.image(this.variant?.imageId, 'auto'); this.updateSticky();
      this.hydrateStock();
    }
    disconnectedCallback() {
      this.stockRevision = (this.stockRevision || 0) + 1;
      this.controller?.abort(); this.galleryObserver?.disconnect();
      if (this.frame) cancelAnimationFrame(this.frame);
      this.ready = false;
    }
    async hydrateStock(force = false) {
      const revision = this.stockRevision = (this.stockRevision || 0) + 1;
      this.stockState = 'loading';
      this.loading(false);
      try {
        const data = await window.TachanCatalog.load({ productId: this.config.productId, productUrl: this.config.productUrl, variantIds: this.variants.map(variant => variant.id), currency: this.config.currency, cartUrl: this.config.cartUrl, force });
        if (revision !== this.stockRevision || !this.isConnected) return;
        this.variants = this.variants.map(variant => {
          const fresh = data.variants.find(item => item.id === String(variant.id));
          return { ...variant, available: fresh.available, price: fresh.price, imageId: fresh.imageId || variant.imageId };
        });
        for (const select of [this.select, ...this.packSelects]) {
          for (const option of select.options) {
            const variant = this.variants.find(item => String(item.id) === option.value);
            const label = option.dataset.ttLabel || option.textContent.split(' — ')[0].trim();
            option.dataset.ttLabel = label;
            option.disabled = !variant?.available;
            if (variant) option.textContent = label + ' — ' + this.money(variant.price) + (variant.available ? '' : ' — ' + this.config.strings.soldOut);
          }
        }
        this.stockState = 'ready';
        this.updateVariant(false);
        this.image(this.variant?.imageId, 'auto');
      } catch {
        if (revision !== this.stockRevision || !this.isConnected) return;
        this.stockState = 'error';
        this.loading(false);
      }
    }
    money(cents) { return this.formatter.format(cents / 100); }
    count() { const count = Number(this.quantity.value); return Number.isFinite(count) ? Math.max(1, Math.floor(count)) : 1; }
    updateVariant(updateUrl) {
      if (this.busy) { this.select.value = String(this.variant.id); return; }
      this.variant = this.variants.find(variant => String(variant.id) === this.select.value) || this.variants[0];
      if (!this.variant) return;
      this.select.value = String(this.variant.id);
      this.packSelects.forEach((select, index) => { if (index === 0 || !this.customPack()) select.value = String(this.variant.id); });
      this.querySelectorAll('[data-tt-unit-price]').forEach(node => { node.textContent = this.money(this.variant.price); });
      this.querySelectorAll('[data-tt-color]').forEach(input => {
        input.checked = input.value === this.variant.options[0];
        input.closest('.tt-swatch')?.classList.toggle('is-selected', input.checked);
        const exact = this.variants.find(variant => variant.options[0] === input.value && variant.options.slice(1).every((value, index) => value === this.variant.options[index + 1]));
        input.disabled = !exact || !exact.available;
      });
      this.updateTotals();
      if (updateUrl) {
        const url = new URL(location.href); url.searchParams.set('variant', this.variant.id); history.replaceState({}, '', url);
        this.image(this.variant.imageId); this.error('');
      }
    }
    discount(count) { return !this.config.offers ? 0 : count >= 3 ? this.config.discountThree : count >= 2 ? this.config.discountTwo : 0; }
    customPack(count = this.count()) { return Boolean(this.config.offers && this.packSelects.length === 3 && (count === 2 || count === 3)); }
    selection(count = this.count()) {
      if (this.customPack(count)) return this.packSelects.slice(0, count).map(select => ({ variant: this.variants.find(item => String(item.id) === select.value), quantity: 1 }));
      return [{ variant: this.variant, quantity: count }];
    }
    selectionAvailable() { return this.selection().every(item => item.variant?.available && item.quantity > 0); }
    selectionTotal(count = this.count()) { return this.selection(count).reduce((sum, item) => sum + Math.max(0, (item.variant?.price || 0) - this.discount(count)) * item.quantity, 0); }
    selectionSaving(count) { return this.selection(count).reduce((sum, item) => sum + Math.min(item.variant?.price || 0, this.discount(count)) * item.quantity, 0); }
    cartItems() {
      const items = new Map();
      this.selection().forEach(({ variant, quantity }) => {
        if (!variant?.available) return;
        const key = String(variant.id), item = items.get(key) || { id: variant.id, quantity: 0 };
        item.quantity += quantity; items.set(key, item);
      });
      return [...items.values()];
    }
    syncPackControls() {
      const active = this.customPack(), count = this.count();
      if (this.packCustom) this.packCustom.hidden = !active;
      this.querySelectorAll('[data-tt-primary-choice], [data-tt-swatches], [data-tt-single-note]').forEach(node => { node.hidden = active; });
      this.packSelects.forEach((select, index) => {
        const visible = active && index < count;
        select.disabled = !visible || Boolean(this.busy);
        const row = select.closest('[data-tt-pack-row]');
        if (row) row.hidden = !visible;
        [...select.options].forEach(option => { option.disabled = !this.variants.find(variant => String(variant.id) === option.value)?.available; });
        const variant = this.variants.find(item => String(item.id) === select.value), price = row?.querySelector('[data-tt-pack-unit-price]');
        if (price) price.textContent = this.money(Math.max(0, (variant?.price || 0) - this.discount(count)));
      });
    }
    updateTotals() {
      if (!this.variant) return;
      const count = this.count();
      this.syncPackControls();
      this.querySelectorAll('[data-tt-total], [data-tt-sticky-price]').forEach(node => { node.textContent = this.money(this.selectionTotal()); });
      this.querySelectorAll('[data-tt-pack-total]').forEach(node => {
        const qty = Number(node.dataset.ttPackTotal); node.textContent = this.money(this.selectionTotal(qty));
      });
      this.querySelectorAll('[data-tt-pack-saving]').forEach(node => {
        const qty = Number(node.dataset.ttPackSaving), saving = this.selectionSaving(qty);
        node.textContent = saving ? `${this.config.strings.saving} ${this.money(saving)}` : this.config.strings.single;
      });
      this.querySelectorAll('[data-tt-pack]').forEach(input => { input.checked = Number(input.value) === count; input.closest('.tt-pack')?.classList.toggle('is-selected', input.checked); });
      this.loading(Boolean(this.busy));
    }
    image(id, behavior = reduced() ? 'auto' : 'smooth') {
      if (!this.gallery || !id) return;
      const slide = [...this.gallery.querySelectorAll('[data-image-id]')].find(node => node.dataset.imageId === String(id));
      if (slide) this.gallery.scrollTo({ left: this.gallery.scrollLeft + slide.getBoundingClientRect().left - this.gallery.getBoundingClientRect().left, behavior });
    }
    updateSticky() {
      if (!this.sticky || !this.buyArea) return;
      const rect = this.buyArea.getBoundingClientRect(), height = innerHeight;
      this.sticky.classList.toggle('is-hidden', (rect.top < height && rect.bottom > 0) || Boolean(this.footer && this.footer.getBoundingClientRect().top < height));
    }
    loading(busy) {
      this.busy = busy;
      const available = this.stockState === 'ready' && this.selectionAvailable();
      const checking = this.stockState === 'loading';
      const locked = busy || this.stockState !== 'ready';
      this.querySelectorAll('[data-tt-submit], [data-tt-checkout]').forEach(button => { button.disabled = busy || !available; });
      this.querySelectorAll('[data-tt-submit-label]').forEach(node => { node.textContent = busy ? this.config.strings.adding : checking ? this.config.stock.loading : this.stockState === 'error' ? this.config.stock.pending : available ? this.config.strings.add : this.config.strings.soldOut; });
      this.select.disabled = locked; this.quantity.disabled = locked;
      const stock = this.querySelector('[data-tt-stock]');
      if (stock) stock.textContent = checking ? this.config.stock.loading : this.stockState === 'error' ? this.config.stock.error : available ? '' : this.config.stock.unavailable;
      const retry = this.querySelector('[data-tt-stock-retry]');
      if (retry) retry.hidden = this.stockState !== 'error' || busy;
      this.querySelectorAll('[data-tt-pack], [data-tt-quantity-step]').forEach(input => { input.disabled = locked || (input.dataset.ttQuantityStep === '-1' && this.count() <= 1); });
      this.querySelectorAll('[data-tt-color]').forEach(input => {
        const exact = this.variants.find(variant => variant.options[0] === input.value && variant.options.slice(1).every((value, index) => value === this.variant.options[index + 1]));
        input.disabled = locked || !exact?.available;
      });
      this.packSelects.forEach((select, index) => { select.disabled = locked || !this.customPack() || index >= this.count(); });
      this.form.setAttribute('aria-busy', String(busy || checking));
    }
    error(message) { const node = this.querySelector('[data-tt-error]'); if (node) { node.textContent = message; node.hidden = !message; } }
    async add(destination) {
      if (this.busy || this.stockState !== 'ready') return;
      this.quantity.value = this.count(); this.updateTotals();
      if (!this.selectionAvailable()) { this.error(this.config.strings.soldOut); return; }
      if (!this.form.reportValidity()) return;
      const custom = this.customPack(), headers = { Accept: 'application/json' };
      let body;
      if (custom) { body = JSON.stringify({ items: this.cartItems() }); headers['Content-Type'] = 'application/json'; }
      else {
        body = new FormData(this.form);
        [...body.keys()].filter(key => key.startsWith('tt_')).forEach(key => body.delete(key));
      }
      this.error(''); this.loading(true);
      try {
        const response = await fetch(this.config.addUrl, { method: 'POST', body, headers, credentials: 'same-origin' });
        const result = await response.json();
        if (!response.ok) throw new Error(typeof result.description === 'string' ? result.description : this.config.strings.error);
        location.assign(destination);
      } catch (error) { this.error(error.message || this.config.strings.error); this.loading(false); }
    }
  });
  initTabs();
  document.addEventListener('shopify:section:load', event => initTabs(event.target));
})();
